import { observer } from "mobx-react";
import { Dialog } from "@/components-v2/Dialog";
import {
  createMeetingRequest,
  createNewMeetingDialogModel,
  DocumentSelection,
  isValidNewMeeting,
  MeetingForm,
  MeetingOption,
  MeetingType,
  newMeetingCaptions,
  NewMeetingDialogModel,
} from "./meetingsDomain";
import { Box, VBox } from "@/components-v2/Box";
import { FC, useMemo, useState } from "react";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { LocalDateTimePicker } from "@/dbot/common/commonComponents";
import { isMobileView } from "@/utils/screen";
import { TMessage } from "@/i18n/tt";
import { MCheckboxGroup } from "@/components-v2/Checkbox";
import { Alert } from "@/components-v2/Alert";
import { Markdown } from "@/text/Markdown";
import { MTextInput } from "@/components-v2/TextInput";
import { DocumentLanguage } from "@/common/common";
import {
  useLanguage,
  useMessageStore,
  useRootStore,
  useTranslation,
} from "@/stores/RootStoreContext";
import { EventDispatcher, thunk } from "@/events/eventsDomain";
import {
  LocalDateFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "@/common/commonForms";
import { LinkButton } from "@/components-v2/Button";
import { InfoButton } from "@/components-v2/InfoButton";
import { LiveGeneralMeetingAnnouncementDialog } from "./generalMeeting/LiveGeneralMeetingAnnouncementDialog";
import { formatDateAndTime } from "@/i18n/dateFormat";
import { findLiveGeneralMeetingPricingInterval } from "./generalMeeting/electronicGeneralMeetingDomain";
import { MSelect } from "@/components-v2/Select";
import { adminProject } from "@/utils/navigation";
import { DateInput } from "@/components-v2/DateInput";
import { CreateMeetingRequest, projectApi } from "@/dbot/common/projectApi";

const NewMeetingDialogInitializer = observer(function NewMeetingDialogInitializer({
  meetingType,
  organisationNumber,
  projectName,
  onClose,
  projectId,
}: {
  meetingType: MeetingType;
  organisationNumber: string;
  projectName: TMessage;
  onClose: () => void;
  projectId?: string;
}) {
  const { t } = useTranslation();
  const { userStore, dispatch } = useRootStore();
  const model: NewMeetingDialogModel = useMemo(
    () =>
      createNewMeetingDialogModel(
        organisationNumber,
        userStore.companiesWithDbotAccess,
        projectId,
        t(projectName),
        meetingType,
        userStore.companyInfo,
        userStore.companyInfo?.documentSettings,
      ),
    [
      organisationNumber,
      t,
      projectName,
      userStore.companyInfo,
      meetingType,
      userStore.companiesWithDbotAccess,
      projectId,
    ],
  );

  const onSubmit = (request: CreateMeetingRequest) => {
    dispatch([
      projectApi.createMeeting(request, {
        onSuccess: (res) => [
          request.duplicateProjectId
            ? {
                type: "load-url",
                url: adminProject({ orgNo: request.orgNo, projectId: res.data.projectId }),
                newWindow: true,
              }
            : {
                type: "navigate",
                url: adminProject({ orgNo: request.orgNo, projectId: res.data.projectId }),
              },
          thunk(() => onClose()),
        ],
        onFailure: () => [],
      }),
    ]);
  };
  return (
    <NewMeetingDialog
      orgNo={organisationNumber}
      meetingType={meetingType}
      onClose={onClose}
      model={model}
      onSubmit={onSubmit}
      projectId={projectId}
      dispatch={dispatch}
    />
  );
});

export type NewMeetingDialogProps = {
  orgNo: string;
  meetingType: MeetingType;
  onClose: () => void;
  model: NewMeetingDialogModel;
  onSubmit: (request: CreateMeetingRequest) => void;
  projectId?: string;
  dispatch: EventDispatcher;
};

export const NewMeetingDialog = observer(function NewMeetingDialog({
  orgNo,
  meetingType,
  onClose,
  onSubmit,
  model,
  projectId,
  dispatch,
}: NewMeetingDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const messageStore = useMessageStore();
  const locale = useLanguage();
  const validateAndCreateNewMeeting = () => {
    if (isValidNewMeeting(model)) {
      setIsLoading(true);
      try {
        onSubmit(
          createMeetingRequest(
            model.targetCompany?.value.organisationNumber ?? orgNo,
            meetingType,
            model,
            projectId,
          ),
        );
      } catch (error) {
        messageStore.showGeneralError();
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Dialog
      fullscreen={isMobileView()}
      maxWidth="sm"
      title={t(newMeetingCaptions[meetingType])}
      onCancel={onClose}
      onSubmit={validateAndCreateNewMeeting}
      submitLabel={t("continue")}
      isSubmitting={isLoading}
      isSubmitDisabled={model.meetingOptions.isNoneSelected}
    >
      <VBox gap="lg">
        {model.targetCompany && (
          <MSelect label={{ key: "dbot.duplicate.copy-to" }} model={model.targetCompany} />
        )}
        <MeetingProjectNameView model={model.projectName} />
        {meetingType === MeetingType.GENERAL_MEETING && (
          <MRadioButtonGroup model={model.ordinaryGeneralMeeting} horizontal />
        )}
        <MeetingFormView model={model.meetingForm} />
        <MeetingDateView
          meetingStartDate={model.meetingStartDate}
          meetingStartTime={model.meetingStartTime}
          meetingEndTime={model.meetingEndTime}
          isCirculationSelected={model.meetingForm.value === MeetingForm.CIRCULATION}
        />
        {meetingType === MeetingType.GENERAL_MEETING ? (
          <VBox>
            {t("new-meeting.meeting-options")}
            <MCheckboxGroup
              model={model.meetingOptions}
              horizontal
              onChange={(_, option, checked) => model.onMeetingOptionChange(option, checked)}
            />
            {model.displayElectronicGMDialog.value && (
              <LiveGeneralMeetingAnnouncementDialog
                shareholderPricingInterval={findLiveGeneralMeetingPricingInterval(
                  Object.values(model.companyInfo?.numberOfSharesPerShareholder ?? []).length,
                )}
                onClose={() => {
                  model.displayElectronicGMDialog.setValue(false);
                  model.onMeetingOptionChange(MeetingOption.MEETING, false);
                }}
                submitLabel={{ key: "release-announcement.general-meeting.order" }}
                onSubmit={() => {
                  dispatch([
                    {
                      type: "order-live-general-meeting",
                      orgNo,
                      generalMeetingDate: formatDateAndTime(
                        locale,
                        model.meetingStartDate.inputValue,
                        model.meetingStartTime.inputValue,
                      ),
                      onSuccess: () => model.displayElectronicGMDialog.setValue(false),
                      onFailure: () => model.displayElectronicGMDialog.setValue(false),
                    },
                  ]);
                }}
                closeLabel={{ key: "cancel" }}
              />
            )}
          </VBox>
        ) : (
          <DocumentSelectionView model={model.documentSelection} />
        )}
        <DocumentLanguageView model={model.meetingDocumentLanguage} />
        {!model.targetCompany &&
          meetingType === MeetingType.GENERAL_MEETING &&
          !model.hasLiveGeneralMeetingFeature && (
            <Alert
              type={"info"}
              message={
                <div>
                  <Markdown value={t("new-meeting.notice.info")} enableLinks />
                  <LinkButton
                    label={t("release-announcement.general-meeting.schedule-more-details")}
                    onClick={() => dispatch([{ type: "showGeneralMeetingAnnouncementDialog" }])}
                  />
                </div>
              }
            />
          )}
      </VBox>
    </Dialog>
  );
});

const MeetingProjectNameView: FC<{ model: StringFieldModel }> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <Box>
        {t("dbot.new-project-name.title")}
        <InfoButton infoText={t("dbot.new-project-name.info")} />
      </Box>
      <MTextInput model={model} />
    </VBox>
  );
});

const MeetingFormView: FC<{ model: RadioFieldModel<MeetingForm> }> = observer(({ model }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      {t("documents.meetingForm.header")}
      <MRadioButtonGroup model={model} horizontal={!isMobileView()} />
    </VBox>
  );
});

const MeetingDateView: FC<{
  meetingStartDate: LocalDateFieldModel;
  meetingStartTime: TimeFieldModel;
  meetingEndTime: TimeFieldModel;
  isCirculationSelected: boolean;
}> = observer(
  ({
    meetingStartDate: meetingStartDate,
    meetingStartTime,
    meetingEndTime,
    isCirculationSelected,
  }) => {
    const { t } = useTranslation();
    return (
      <>
        {isCirculationSelected ? (
          <VBox>
            <Box>
              <div>{t("documents.boardMeeting.date-circulation")}</div>
            </Box>
            <DateInput
              id={"meetingDate"}
              onInput={(event) => meetingStartDate.setInputValue(event.target.value)}
              onChange={(event) => meetingStartDate.setAndCommitInputValue(event.input)}
              value={meetingStartDate.inputValue}
              error={!!meetingStartDate.error}
              message={meetingStartDate.error ? t(meetingStartDate.error) : undefined}
              onBlur={() => {
                meetingStartDate.commit();
              }}
            />
          </VBox>
        ) : (
          <LocalDateTimePicker
            dateLabel={{ key: "date" }}
            dateField={meetingStartDate}
            timeLabel={{ key: "documents.boardMeeting.dateBoardMeetingStartTimeLabel" }}
            timeField={meetingStartTime}
            endTimeLabel={{ key: "documents.boardMeeting.dateBoardMeetingEndTimeLabel" }}
            endTimeField={meetingEndTime}
          />
        )}
      </>
    );
  },
);

const DocumentSelectionView: FC<{ model: RadioFieldModel<DocumentSelection> }> = observer(
  ({ model }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        {t("new-meeting.documents-selection")}
        <MRadioButtonGroup model={model} horizontal />
      </VBox>
    );
  },
);

const DocumentLanguageView: FC<{ model: RadioFieldModel<DocumentLanguage> }> = observer(
  ({ model }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        {t("new-meeting.documents-language")}
        <MRadioButtonGroup model={model} horizontal={!isMobileView()} />
      </VBox>
    );
  },
);

export default NewMeetingDialogInitializer;
